import { useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Calendar } from "@/components/ui/calendar";
import { Popover, PopoverTrigger, PopoverContent } from "@/components/ui/popover";
import { Calendar as CalendarIcon, Pencil, XCircle } from "lucide-react";
import { money } from "@/lib/currency";

function isFuture(iso) {
  if (!iso) return false;
  try {
    const d = new Date(iso + "T00:00:00");
    d.setHours(0, 0, 0, 0);
    const t = new Date();
    t.setHours(0, 0, 0, 0);
    return d >= t;
  } catch { return false; }
}

export default function BookingActions({ booking, onChanged }) {
  const [openCancel, setOpenCancel] = useState(false);
  const [openModify, setOpenModify] = useState(false);
  const [quote, setQuote] = useState(null);
  const [checkIn, setCheckIn] = useState(booking.check_in);
  const [checkOut, setCheckOut] = useState(booking.check_out || "");
  const [guests, setGuests] = useState(booking.guests);
  const [busy, setBusy] = useState(false);

  const isCancelled = booking.status === "cancelled";
  const cancellable = !isCancelled && isFuture(booking.check_in);

  useEffect(() => {
    if (!openCancel) return;
    api.get(`/bookings/${booking.id}/refund-quote`).then((r) => setQuote(r.data)).catch(() => setQuote(null));
  }, [openCancel, booking.id]);

  const doCancel = async (e) => {
    e?.stopPropagation();
    setBusy(true);
    try {
      await api.post(`/bookings/${booking.id}/cancel`);
      toast.success("Booking cancelled");
      setOpenCancel(false);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not cancel");
    } finally { setBusy(false); }
  };

  const doModify = async (e) => {
    e?.stopPropagation();
    if (!checkIn) { toast.error("Pick a check-in date"); return; }
    if (checkOut && checkOut <= checkIn) { toast.error("Check-out must be after check-in"); return; }
    setBusy(true);
    try {
      await api.put(`/bookings/${booking.id}`, { check_in: checkIn, check_out: checkOut || null, guests });
      toast.success("Booking updated");
      setOpenModify(false);
      onChanged?.();
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || "Could not modify");
    } finally { setBusy(false); }
  };

  if (isCancelled) {
    let refundLine;
    if (booking.refund_tier && booking.refund_amount > 0) {
      refundLine = `· Refund of ${money(booking.refund_amount)} pending`;
    } else if (booking.refund_tier === "unpaid") {
      refundLine = "· No payment was made";
    } else {
      refundLine = "· Non-refundable";
    }
    return (
      <div className="mt-4 text-xs text-muted2 text-center border-t border-subtle pt-3" data-testid={`cancelled-note-${booking.id}`}>
        Cancelled {refundLine}
      </div>
    );
  }
  if (!cancellable) return null;

  return (
    <div className="mt-4 pt-3 border-t border-subtle grid grid-cols-2 gap-2">
      <Button
        data-testid={`modify-btn-${booking.id}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpenModify(true); }}
        variant="outline"
        size="sm"
        className="rounded-full bg-white border-subtle hover:border-moss text-xs"
      >
        <Pencil className="h-3.5 w-3.5 mr-1.5" /> Modify
      </Button>
      <Button
        data-testid={`cancel-btn-${booking.id}`}
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); setOpenCancel(true); }}
        variant="outline"
        size="sm"
        className="rounded-full bg-white border-subtle hover:border-destructive hover:text-destructive text-xs"
      >
        <XCircle className="h-3.5 w-3.5 mr-1.5" /> Cancel
      </Button>

      {/* Cancel dialog */}
      <Dialog open={openCancel} onOpenChange={setOpenCancel}>
        <DialogContent data-testid="cancel-dialog" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Cancel this booking?</DialogTitle>
            <DialogDescription asChild>
              <div>
                <div className="text-sm text-muted2 mt-2">{booking.experience_title}</div>
                <div className="text-xs text-muted2">Check-in {booking.check_in} · {booking.guests} guest(s)</div>
              </div>
            </DialogDescription>
          </DialogHeader>

          <div className="rounded-xl bg-canvas border border-subtle p-4 my-2">
            <div className="overline">Refund estimate</div>
            {quote ? (
              <div className="mt-2">
                <div className="font-serif text-3xl">${(quote.amount || 0).toFixed(0)}</div>
                <div className="text-xs text-muted2 mt-1">{quote.reason}</div>
                <div className="text-[10px] text-muted2 mt-2">
                  Policy: 14+ days = full · 7–13 days = 50% · under 7 days = non-refundable
                </div>
              </div>
            ) : <div className="text-xs text-muted2 mt-2">Computing refund…</div>}
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpenCancel(false)} className="rounded-full">Keep booking</Button>
            <Button
              data-testid="confirm-cancel-btn"
              onClick={doCancel}
              disabled={busy}
              className="rounded-full bg-destructive hover:bg-destructive/90 text-white"
            >
              {busy ? "Cancelling…" : "Confirm cancellation"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modify dialog */}
      <Dialog open={openModify} onOpenChange={setOpenModify}>
        <DialogContent data-testid="modify-dialog" onClick={(e) => e.stopPropagation()}>
          <DialogHeader>
            <DialogTitle className="font-serif text-2xl">Modify this booking</DialogTitle>
            <DialogDescription asChild>
              <div>
                <div className="text-sm text-muted2 mt-2">{booking.experience_title}</div>
                <div className="text-[11px] text-muted2 mt-1">Room, package, and add-ons stay the same. To change those, cancel and rebook.</div>
              </div>
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 my-2">
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Check-in</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button data-testid="modify-checkin" type="button" className="mt-1.5 w-full flex items-center gap-2 rounded-xl bg-white border border-subtle px-3 py-2 text-left text-sm hover:border-moss">
                      <CalendarIcon className="h-4 w-4 text-muted2" />
                      {checkIn || <span className="text-muted2">Pick date</span>}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 bg-white" align="start">
                    <Calendar
                      mode="single"
                      selected={checkIn ? new Date(checkIn + "T00:00:00") : undefined}
                      onSelect={(d) => {
                        if (!d) return;
                        const iso = d.toISOString().split("T")[0];
                        setCheckIn(iso);
                        if (checkOut && checkOut <= iso) setCheckOut("");
                      }}
                      disabled={(date) => date < new Date(new Date().toDateString())}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
              <div>
                <Label className="text-xs">Check-out</Label>
                <Popover>
                  <PopoverTrigger asChild>
                    <button data-testid="modify-checkout" type="button" className="mt-1.5 w-full flex items-center gap-2 rounded-xl bg-white border border-subtle px-3 py-2 text-left text-sm hover:border-moss">
                      <CalendarIcon className="h-4 w-4 text-muted2" />
                      {checkOut || <span className="text-muted2">Optional</span>}
                    </button>
                  </PopoverTrigger>
                  <PopoverContent className="p-0 bg-white" align="start">
                    <Calendar
                      mode="single"
                      selected={checkOut ? new Date(checkOut + "T00:00:00") : undefined}
                      onSelect={(d) => d && setCheckOut(d.toISOString().split("T")[0])}
                      disabled={(date) => {
                        const iso = date.toISOString().split("T")[0];
                        if (checkIn && iso <= checkIn) return true;
                        return date < new Date(new Date().toDateString());
                      }}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
            <div>
              <Label className="text-xs">Number of guests</Label>
              <Input
                data-testid="modify-guests"
                type="number"
                min="1"
                value={guests}
                onChange={(e) => setGuests(Math.max(1, parseInt(e.target.value) || 1))}
                className="mt-1.5 rounded-xl bg-white border-subtle"
              />
            </div>
          </div>

          <DialogFooter className="gap-2">
            <Button variant="outline" onClick={() => setOpenModify(false)} className="rounded-full">Cancel</Button>
            <Button
              data-testid="confirm-modify-btn"
              onClick={doModify}
              disabled={busy}
              className="rounded-full bg-moss hover:bg-moss-hover text-canvas"
            >
              {busy ? "Saving…" : "Save changes"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
